<script lang="ts">
/**
 * Markdown drawn from shared/markdown.ts's tree with Vue's own elements, never
 * `v-html`: whatever the text says, it can only ever be text on the page. A link
 * goes only to http(s) or mailto (`safeHref`), in a new tab. A code block has a
 * Copy of its own, which takes the code as written, not what a selection gets.
 */
import { computed, defineComponent, h, type VNode, type VNodeChild } from "vue";
import { parseMarkdown, type MdBlock, type MdInline } from "@shared/markdown.js";
import { copyText } from "../lib/clipboard.js";
import { toast } from "../lib/toast.js";

async function copyCode(text: string): Promise<void> {
  await copyText(text);
  toast("Code copied");
}

function inline(c: readonly MdInline[]): VNodeChild[] {
  return c.map((x): VNodeChild => {
    if (typeof x === "string") return x;
    switch (x.t) {
      case "br":
        return h("br");
      case "code":
        return h("code", x.text);
      case "b":
        return h("strong", inline(x.c));
      case "i":
        return h("em", inline(x.c));
      case "s":
        return h("del", inline(x.c));
      case "a":
        return x.href ? h("a", { href: x.href, target: "_blank", rel: "noopener noreferrer" }, inline(x.c)) : h("span", inline(x.c));
    }
  });
}

function block(b: MdBlock): VNode {
  switch (b.t) {
    case "p":
      return h("p", inline(b.c));
    case "h":
      return h(`h${b.level}`, inline(b.c));
    case "code":
      return h("div", { class: "md-codebox" }, [
        h("pre", { class: "md-code", "data-lang": b.lang || undefined }, h("code", b.text)),
        h(
          "button",
          { class: "md-copy", type: "button", title: "Copy this code", onPointerdown: (e: Event) => e.preventDefault(), onClick: () => void copyCode(b.text) },
          "Copy",
        ),
      ]);
    case "quote":
      return h("blockquote", b.c.map(block));
    case "hr":
      return h("hr");
    case "list":
      return h(
        b.ordered ? "ol" : "ul",
        b.ordered && b.start !== 1 ? { start: b.start } : {},
        b.items.map((item) =>
          h("li", item.checked === undefined ? {} : { class: "md-task" }, [
            item.checked === undefined ? null : h("input", { type: "checkbox", checked: item.checked, disabled: true }),
            ...item.c.map(block),
          ]),
        ),
      );
    case "table": {
      const style = (k: number) => (b.align[k] ? { textAlign: b.align[k]! } : undefined);
      return h("div", { class: "md-table" }, [
        h("table", [
          h("thead", h("tr", b.head.map((c, k) => h("th", { style: style(k) }, inline(c))))),
          h("tbody", b.rows.map((row) => h("tr", row.map((c, k) => h("td", { style: style(k) }, inline(c)))))),
        ]),
      ]);
    }
  }
}

export default defineComponent({
  name: "MarkdownText",
  props: { text: { type: String, required: true } },
  setup(props) {
    const blocks = computed(() => parseMarkdown(props.text));
    return () => h("div", { class: "md" }, blocks.value.map(block));
  },
});
</script>
